import { useCallback } from 'react';
import type { MiniCheckpoint, MiniCheckpointResult } from '../types';
import { tierTitle } from '../lib/curriculum';
import { useEnterAction } from '../lib/useEnterAction';

interface MiniCheckpointSummaryProps {
  result: MiniCheckpointResult;
  checkpoint: MiniCheckpoint;
  onRepeat: () => void;
  onBack: () => void;
}

export default function MiniCheckpointSummary({
  result,
  checkpoint,
  onRepeat,
  onBack,
}: MiniCheckpointSummaryProps) {
  const onEnterPrimary = useCallback(() => {
    if (!result.passed) onRepeat();
    else onBack();
  }, [result.passed, onRepeat, onBack]);

  useEnterAction(onEnterPrimary);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 animate-fade-in-up">
      <header className="text-center">
        <p className="label text-indigo-700 dark:text-indigo-300">
          Mini-checkpoint · {tierTitle(checkpoint.tierId)}
        </p>
        <h1 className="mt-1 text-3xl font-semibold text-strong">
          {checkpoint.title}
        </h1>
      </header>

      <div
        className={`rounded-2xl p-6 text-center ring-1 ${
          result.passed
            ? 'bg-emerald-100 text-emerald-900 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-200 dark:ring-emerald-500/30'
            : 'bg-rose-100 text-rose-900 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-200 dark:ring-rose-500/30'
        }`}
      >
        <p className="text-4xl" aria-hidden>
          {result.passed ? '✓' : '○'}
        </p>
        <p className="mt-2 text-lg font-semibold">
          {result.passed ? 'Port bestået!' : 'Ikke bestået endnu'}
        </p>
        <p className="mt-2 text-sm opacity-90">
          {result.wordsCompleted} ord · {(result.accuracy * 100).toFixed(0)}%
          nøjagtighed
          {result.wordsSkipped > 0
            ? ` · ${result.wordsSkipped} sprunget over`
            : ''}
        </p>
        {!result.passed && (
          <p className="mt-2 text-xs opacity-80">
            Kræver mindst {checkpoint.minWords} ord og{' '}
            {(checkpoint.minAccuracy * 100).toFixed(0)}% nøjagtighed.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        {!result.passed && (
          <button
            type="button"
            onClick={onRepeat}
            className="flex-1 rounded-xl bg-indigo-500 px-4 py-3 font-semibold text-white shadow-sm transition hover:bg-indigo-400"
          >
            Prøv igen
          </button>
        )}
        <button
          type="button"
          onClick={onBack}
          className={
            result.passed
              ? 'flex-1 rounded-xl bg-indigo-500 px-4 py-3 font-semibold text-white shadow-sm transition hover:bg-indigo-400'
              : 'flex-1 btn-secondary py-3 font-semibold'
          }
        >
          Tilbage til oversigt
        </button>
      </div>
      <p className="text-center text-xs text-faint">
        Enter: {result.passed ? 'tilbage til oversigt' : 'prøv igen'}
      </p>
    </div>
  );
}
